import { useState, useEffect, type ReactNode } from "react";
import { useSettings } from "./useSettings.js";

// ── Feld-Spezifikation ─────────────────────────────────────────────────────────

export type FieldSpec =
  | { kind: "group"; label: string }
  | { kind: "text"; key: string; label: string; placeholder?: string; help?: string }
  | { kind: "textarea"; key: string; label: string; placeholder?: string; help?: string; rows?: number }
  | { kind: "number"; key: string; label: string; help?: string; min?: number; max?: number; step?: number; suffix?: string }
  | { kind: "select"; key: string; label: string; help?: string; options: { value: string; label: string }[] }
  | { kind: "checkbox"; key: string; label: string; help?: string }
  | { kind: "readonly"; key: string; label: string; help?: string };

interface SettingsFormProps {
  category: string;
  title: string;
  intro?: ReactNode;
  fields: FieldSpec[];
  /** Zusätzlicher Block über dem Speichern-Bereich (z. B. Hinweise). */
  footer?: ReactNode;
}

// ── Hilfs-Styles (an global.css angelehnt) ─────────────────────────────────────

const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: "var(--color-text-muted)", marginBottom: 4, display: "block" };
const helpStyle: React.CSSProperties = { fontSize: 12, color: "var(--color-text-muted)", marginTop: 4 };

export function SettingsForm({ category, title, intro, fields, footer }: SettingsFormProps) {
  const { values, loading, saving, error, saved, save } = useSettings(category);
  const [draft, setDraft] = useState<Record<string, unknown>>({});

  // Server-Werte in den Entwurf übernehmen, sobald geladen / nach dem Speichern.
  useEffect(() => {
    if (values) setDraft(values);
  }, [values]);

  const dirty = values
    ? Object.keys(draft).some((k) => JSON.stringify(draft[k]) !== JSON.stringify(values[k]))
    : false;

  const set = (key: string, value: unknown) => setDraft((d) => ({ ...d, [key]: value }));

  const onSave = () => {
    // Nur geänderte Schlüssel als Patch senden.
    const patch: Record<string, unknown> = {};
    if (values) {
      for (const k of Object.keys(draft)) {
        if (JSON.stringify(draft[k]) !== JSON.stringify(values[k])) patch[k] = draft[k];
      }
    }
    void save(patch);
  };

  if (loading) {
    return (
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>{title}</h2>
        <div style={{ color: "var(--color-text-muted)", fontSize: 13 }}>Lade Einstellungen…</div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{title}</h2>
      {intro && <div style={{ fontSize: 13, color: "var(--color-text-muted)", marginBottom: 20, maxWidth: 720 }}>{intro}</div>}

      <div className="card" style={{ maxWidth: 720 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {fields.map((f, i) => {
            if (f.kind === "group") {
              return (
                <div key={`g${i}`} style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--color-text-muted)", borderBottom: "1px solid var(--color-border)", paddingBottom: 6, marginTop: i === 0 ? 0 : 8 }}>
                  {f.label}
                </div>
              );
            }

            const v = draft[f.key];

            if (f.kind === "checkbox") {
              return (
                <label key={f.key} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(v)}
                    onChange={(e) => set(f.key, e.target.checked)}
                    style={{ width: 16, height: 16, flexShrink: 0 }}
                  />
                  <span>
                    <span style={{ fontSize: 14, color: "var(--color-text)" }}>{f.label}</span>
                    {f.help && <span style={{ ...helpStyle, marginTop: 0, display: "block" }}>{f.help}</span>}
                  </span>
                </label>
              );
            }

            return (
              <div key={f.key}>
                <label style={labelStyle}>{f.label}</label>
                {f.kind === "text" && (
                  <input type="text" value={String(v ?? "")} placeholder={f.placeholder ?? ""} onChange={(e) => set(f.key, e.target.value)} />
                )}
                {f.kind === "textarea" && (
                  <textarea value={String(v ?? "")} placeholder={f.placeholder ?? ""} rows={f.rows ?? 4} onChange={(e) => set(f.key, e.target.value)} style={{ resize: "vertical", fontFamily: "inherit" }} />
                )}
                {f.kind === "number" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="number"
                      value={v === undefined || v === null ? "" : Number(v)}
                      min={f.min} max={f.max} step={f.step ?? "any"}
                      onChange={(e) => set(f.key, e.target.value === "" ? "" : Number(e.target.value))}
                    />
                    {f.suffix && <span style={{ fontSize: 13, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>{f.suffix}</span>}
                  </div>
                )}
                {f.kind === "select" && (
                  <select value={String(v ?? "")} onChange={(e) => set(f.key, e.target.value)}>
                    {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                )}
                {f.kind === "readonly" && (
                  <input type="text" value={String(v ?? "")} disabled style={{ background: "var(--color-bg)", color: "var(--color-text-muted)" }} />
                )}
                {f.help && <div style={helpStyle}>{f.help}</div>}
              </div>
            );
          })}
        </div>

        {footer && <div style={{ marginTop: 20 }}>{footer}</div>}

        {/* Speichern-Leiste */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--color-border)" }}>
          <button className="btn-primary" onClick={onSave} disabled={!dirty || saving}>
            {saving ? "Speichern…" : "Speichern"}
          </button>
          {dirty && !saving && <span style={{ fontSize: 13, color: "var(--color-warning)" }}>Ungespeicherte Änderungen</span>}
          {saved && !dirty && <span style={{ fontSize: 13, color: "var(--color-success)" }}>✓ Gespeichert</span>}
          {error && <span style={{ fontSize: 13, color: "var(--color-error)" }}>{error}</span>}
        </div>
      </div>
    </div>
  );
}
